import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Skeleton,
  TextField,
  Tooltip,
  Menu,
  MenuItem,
  Grid,
  ListItemIcon,
  ListItemText,
  Checkbox,
} from "@mui/material";
import {
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Add as AddIcon,
  ChatBubble as ChatBubbleIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Storage as StorageIcon,
  Language as LanguageIcon,
} from "@mui/icons-material";
import { collectionsApi, documentsApi } from "../services/rag-service";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "react-toastify";
import TaskProgressDialog from "../components/TaskProgressDialog";
import UploadDocumentsDialog from "../components/UploadDocumentsDialog";
import { useProjects } from "../contexts/ProjectContext";
import { PROJECT_SELECTION_CHANGED_EVENT } from "../components/ProjectSwitcher";
import { useTranslation } from "react-i18next";
import { isUserAdmin } from "../utils/auth-helpers";
import { getIntlLocale } from "../utils/language";

const CollectionDetailPage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const { collectionId } = useParams();
  const navigate = useNavigate();
  const { selectedProjects } = useProjects();
  const [collection, setCollection] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // New states for upload dropdown menu and external source modal
  const [uploadMenuAnchorEl, setUploadMenuAnchorEl] = useState(null);
  const [isExternalSourceModalOpen, setIsExternalSourceModalOpen] =
    useState(false);
  const [externalSourceData, setExternalSourceData] = useState({
    collection_id: collectionId,
    source_type: "s3",
    s3_config: {
      bucket_name: "",
      prefix: "",
      access_key: "",
      secret_key: "",
      region: "",
      endpoint_url: "",
    },
  });
  const [processingExternalSource, setProcessingExternalSource] =
    useState(false);

  // Web import states
  const [isWebImportModalOpen, setIsWebImportModalOpen] = useState(false);
  const [webImportType, setWebImportType] = useState("single"); // 'single' or 'batch'
  const [webImportData, setWebImportData] = useState({
    collection_id: collectionId,
    url: "",
    urls: [""],
    crawl_level: 1,
    max_links: 10,
    max_links_per_url: 10,
    restrict_domain: true,
  });
  const [processingWebImport, setProcessingWebImport] = useState(false);

  // Web import validation states
  const [webImportValidation, setWebImportValidation] = useState({
    url: { isValid: true, error: "", warning: "" },
    urls: [{ isValid: true, error: "", warning: "" }],
    crawl_level: { isValid: true, error: "" },
    max_links: { isValid: true, error: "" },
    max_links_per_url: { isValid: true, error: "" },
  });

  // Web import touched states (track which fields have been interacted with)
  const [webImportTouched, setWebImportTouched] = useState({
    url: false,
    urls: [false],
    crawl_level: false,
    max_links: false,
    max_links_per_url: false,
  });

  // Task progress tracking
  const [taskId, setTaskId] = useState(null);
  const [isTaskProgressDialogOpen, setIsTaskProgressDialogOpen] =
    useState(false);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalDocuments, setTotalDocuments] = useState(0);

  // Document deletion confirmation states
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [deletingDocument, setDeletingDocument] = useState(false);

  // Check user permissions based on access level
  const isAdmin = isUserAdmin();
  const canUpload =
    isAdmin ||
    collection?.access_level === "admin" ||
    collection?.access_level === "modifier";
  const canDelete = isAdmin || collection?.access_level === "admin";

  // Skeleton for table rows while loading
  const TableRowSkeleton = ({ colCount = 6 }) => (
    <TableRow>
      {[...Array(colCount)].map((_, index) => (
        <TableCell align={i18n.dir() === "rtl" ? "right" : "left"} key={index}>
          <Skeleton animation="wave" variant="text" />
        </TableCell>
      ))}
    </TableRow>
  );

  // Fetch collection details
  const fetchCollection = useCallback(async () => {
    try {
      setError(null);
      const data = await collectionsApi.getCollection(
        collectionId,
        selectedProjects,
      );
      setCollection(data);
      // Update total documents count from collection data
      if (data.doc_count !== undefined) {
        setTotalDocuments(data.doc_count);
      }
    } catch (err) {
      console.error("Error fetching collection:", err);
      setError(t("collections.failedToLoadCollection"));
      toast.error(t("collections.failedToLoadCollection"));
    }
  }, [collectionId, selectedProjects]);

  // Fetch documents in the collection with pagination
  const fetchDocuments = useCallback(async () => {
    if (!collection) return; // Don't fetch if collection isn't loaded yet

    try {
      setDocumentLoading(true);
      const skip = page * rowsPerPage;
      const limit = rowsPerPage;

      const response = await documentsApi.getDocumentsByCollection(
        collectionId,
        {
          skip,
          limit,
        },
      );

      // Handle new response format with items array and pagination metadata
      if (response.items) {
        setDocuments(response.items);
        setTotalDocuments(response.total);
      } else {
        // Fallback for backward compatibility
        setDocuments(response);
      }

      setError(null); // Clear any previous errors
    } catch (err) {
      console.error("Error fetching documents:", err);
      setError(t("collections.failedToLoadDocuments"));
      toast.error(t("collections.failedToLoadDocuments"));
      // Keep the old documents in state if we fail to fetch new ones
    } finally {
      setDocumentLoading(false);
    }
  }, [collectionId, page, rowsPerPage, collection]);

  // Initial data loading
  useEffect(() => {
    const loadCollectionData = async () => {
      setLoading(true);
      try {
        // Fetch collection data
        const collectionData = await collectionsApi.getCollection(collectionId);
        setCollection(collectionData);

        // Update total documents count from collection data
        if (collectionData.doc_count !== undefined) {
          setTotalDocuments(collectionData.doc_count);
        }

        setError(null);
      } catch (err) {
        console.error("Error loading collection data:", err);
        setError(t("collections.failedToLoadCollection"));
      } finally {
        setLoading(false);
      }
    };

    loadCollectionData();
  }, [fetchCollection, collectionId]);

  // Add listener for project selection changes
  useEffect(() => {
    const handleProjectChange = (event) => {
      // Only refresh if we're still on the collection detail page
      if (event.detail.path.includes("/collections/")) {
        fetchCollection();
      }
    };

    // Add listener for project selection changes
    window.addEventListener(
      PROJECT_SELECTION_CHANGED_EVENT,
      handleProjectChange,
    );

    // Clean up listener on unmount
    return () => {
      window.removeEventListener(
        PROJECT_SELECTION_CHANGED_EVENT,
        handleProjectChange,
      );
    };
  }, [fetchCollection]);

  // Validate form when import type changes
  useEffect(() => {
    // Re-validate form when switching between single and batch mode
    if (webImportType === "single") {
      // Validate single URL
      const urlValidation = validateUrl(webImportData.url);
      setWebImportValidation((prev) => ({
        ...prev,
        url: urlValidation,
      }));
    } else {
      // Validate all batch URLs
      const urlValidations = webImportData.urls.map((url) => validateUrl(url));
      setWebImportValidation((prev) => ({
        ...prev,
        urls: urlValidations,
      }));

      // Reset touched state for URLs when switching to batch mode
      setWebImportTouched((prev) => ({
        ...prev,
        urls: webImportData.urls.map(() => false),
      }));
    }
  }, [webImportType, webImportData.url, webImportData.urls]);

  // Load documents whenever pagination changes or collection changes
  useEffect(() => {
    // Skip initial load until collection is loaded
    if (!collection) return;

    const loadDocuments = async () => {
      setDocumentLoading(true);
      try {
        const skip = page * rowsPerPage;
        const limit = rowsPerPage;

        const documentsResponse = await documentsApi.getDocumentsByCollection(
          collectionId,
          {
            skip,
            limit,
          },
        );

        // Handle new response format with items array and pagination metadata
        if (documentsResponse.items) {
          setDocuments(documentsResponse.items);
          if (documentsResponse.total !== undefined) {
            setTotalDocuments(documentsResponse.total);
          }
        } else {
          // Fallback for backward compatibility
          setDocuments(documentsResponse);
        }

        setError(null);
      } catch (err) {
        console.error("Error loading documents:", err);
        setError(t("collections.failedToLoadDocuments"));
        toast.error(t("collections.failedToLoadDocuments"));
      } finally {
        setDocumentLoading(false);
      }
    };

    loadDocuments();
  }, [collectionId, page, rowsPerPage, collection]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Refresh documents list and collection details (for updated count)
  const handleDocumentsUploaded = () => {
    fetchCollection();
    fetchDocuments();
  };

  // Handle document deletion - show confirmation modal
  const handleDeleteDocument = (documentId, documentTitle) => {
    if (!canDelete) {
      toast.error(t("collections.noPermissionToDelete"));
      return;
    }

    // Find the document to get its details
    const document = documents.find((doc) => doc.id === documentId);
    setDocumentToDelete(document);
    setDeleteConfirmOpen(true);
  };

  // Handle confirmed document deletion
  const handleConfirmDocumentDelete = async () => {
    if (!documentToDelete) return;

    setDeletingDocument(true);
    try {
      await documentsApi.deleteDocument(documentToDelete.id);

      // Refresh documents list and collection details
      setDocuments((prevDocs) =>
        prevDocs.filter((doc) => doc.id !== documentToDelete.id),
      );
      fetchCollection();

      toast.success(t("collections.documentDeletedSuccess"));
      setDeleteConfirmOpen(false);
      setDocumentToDelete(null);
    } catch (err) {
      console.error("Error deleting document:", err);
      toast.error(t("collections.failedToDeleteDocument"));
    } finally {
      setDeletingDocument(false);
    }
  };

  // Handle cancel document deletion
  const handleCancelDocumentDelete = () => {
    setDeleteConfirmOpen(false);
    setDocumentToDelete(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const lang = i18n.language || "en";
    return date.toLocaleDateString(getIntlLocale(lang), {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get status color based on document status
  const getStatusColor = (status) => {
    switch (status) {
      case "processed":
        return "success";
      case "processing":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const getDocumentStatusLabel = (document) => {
    return document?.status_label || document?.status || "";
  };

  // Handle upload menu
  const handleUploadMenuOpen = (event) => {
    setUploadMenuAnchorEl(event.currentTarget);
  };

  const handleUploadMenuClose = () => {
    setUploadMenuAnchorEl(null);
  };

  const handleUploadDocumentClick = () => {
    handleUploadMenuClose();
    setIsUploadModalOpen(true);
  };

  const handleExternalSourceClick = () => {
    handleUploadMenuClose();
    setIsExternalSourceModalOpen(true);
  };

  const handleWebImportClick = () => {
    handleUploadMenuClose();
    setIsWebImportModalOpen(true);
  };

  // Handle S3 config changes
  const handleS3ConfigChange = (field, value) => {
    setExternalSourceData({
      ...externalSourceData,
      s3_config: {
        ...externalSourceData.s3_config,
        [field]: value,
      },
    });
  };

  // Handle external source upload
  const handleUploadFromExternalSource = async () => {
    setProcessingExternalSource(true);
    try {
      // Ensure collection ID is set
      const payload = {
        ...externalSourceData,
        collection_id: collectionId,
      };

      const response = await documentsApi.uploadFromExternalSource(payload);

      // Close external source modal
      setIsExternalSourceModalOpen(false);

      // Reset form
      setExternalSourceData({
        collection_id: collectionId,
        source_type: "s3",
        s3_config: {
          bucket_name: "",
          prefix: "",
          access_key: "",
          secret_key: "",
          region: "",
          endpoint_url: "",
        },
      });

      // Show success message
      toast.success(t("collections.documentProcessingStarted"));

      // Open task progress dialog with the task ID from response
      if (response && response.task_id) {
        setTaskId(response.task_id);
        setIsTaskProgressDialogOpen(true);
      }

      // Refresh collection details after a delay
      setTimeout(() => {
        fetchCollection();
      }, 2000);
    } catch (err) {
      console.error("Error starting external source import:", err);
      setError(t("collections.failedToStartImportConfig"));
      const details = err.response?.data?.detail ?? "";
      toast.error(t("collections.failedToStartImportDetails", { details }));
    } finally {
      setProcessingExternalSource(false);
    }
  };

  // URL validation helper functions
  const isValidUrl = (urlString) => {
    try {
      const url = new URL(urlString);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  };

  const isSecureUrl = (urlString) => {
    try {
      const url = new URL(urlString);
      return url.protocol === "https:";
    } catch {
      return false;
    }
  };

  const validateUrl = (url) => {
    if (!url.trim()) {
      return {
        isValid: false,
        error: t("collections.urlRequired"),
        warning: "",
      };
    }

    if (!isValidUrl(url)) {
      return {
        isValid: false,
        error: t("collections.urlInvalid"),
        warning: "",
      };
    }

    if (!isSecureUrl(url)) {
      return {
        isValid: true,
        error: "",
        warning: t("collections.urlNotSecureWarning"),
      };
    }

    return { isValid: true, error: "", warning: "" };
  };

  const validateNumber = (value, min, fieldName) => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < min) {
      return {
        isValid: false,
        error: t("collections.numberFieldMin", { field: fieldName, min }),
      };
    }
    return { isValid: true, error: "" };
  };

  // Handle web import form changes
  const handleWebImportChange = (field, value) => {
    setWebImportData({
      ...webImportData,
      [field]: value,
    });

    // Validate the changed field (always validate for internal state, but only show errors if touched)
    let newValidation = { ...webImportValidation };

    if (field === "url") {
      newValidation.url = validateUrl(value);
    } else if (field === "crawl_level") {
      newValidation.crawl_level = validateNumber(
        value,
        1,
        t("collections.crawlLevel"),
      );
    } else if (field === "max_links") {
      newValidation.max_links = validateNumber(
        value,
        1,
        t("collections.maxLinks"),
      );
    } else if (field === "max_links_per_url") {
      newValidation.max_links_per_url = validateNumber(
        value,
        1,
        t("collections.maxLinksPerUrl"),
      );
    }

    setWebImportValidation(newValidation);
  };

  // Handle field blur (when user moves away from field)
  const handleWebImportBlur = (field) => {
    setWebImportTouched({
      ...webImportTouched,
      [field]: true,
    });
  };

  // Handle URL changes for batch import
  const handleBatchUrlChange = (index, value) => {
    const newUrls = [...webImportData.urls];
    newUrls[index] = value;
    setWebImportData({
      ...webImportData,
      urls: newUrls,
    });

    // Validate the changed URL
    const newUrlValidations = [...webImportValidation.urls];
    newUrlValidations[index] = validateUrl(value);
    setWebImportValidation({
      ...webImportValidation,
      urls: newUrlValidations,
    });
  };

  // Handle batch URL blur (when user moves away from URL field)
  const handleBatchUrlBlur = (index) => {
    const newTouchedUrls = [...webImportTouched.urls];
    newTouchedUrls[index] = true;
    setWebImportTouched({
      ...webImportTouched,
      urls: newTouchedUrls,
    });
  };

  // Add new URL field for batch import
  const handleAddBatchUrl = () => {
    setWebImportData({
      ...webImportData,
      urls: [...webImportData.urls, ""],
    });

    // Add validation and touched state for new URL field
    setWebImportValidation({
      ...webImportValidation,
      urls: [
        ...webImportValidation.urls,
        { isValid: true, error: "", warning: "" },
      ],
    });

    setWebImportTouched({
      ...webImportTouched,
      urls: [...webImportTouched.urls, false],
    });
  };

  // Remove URL field for batch import
  const handleRemoveBatchUrl = (index) => {
    if (webImportData.urls.length > 1) {
      const newUrls = webImportData.urls.filter((_, i) => i !== index);
      const newUrlValidations = webImportValidation.urls.filter(
        (_, i) => i !== index,
      );
      const newTouchedUrls = webImportTouched.urls.filter(
        (_, i) => i !== index,
      );

      setWebImportData({
        ...webImportData,
        urls: newUrls,
      });

      setWebImportValidation({
        ...webImportValidation,
        urls: newUrlValidations,
      });

      setWebImportTouched({
        ...webImportTouched,
        urls: newTouchedUrls,
      });
    }
  };

  // Helper functions for validation display
  const shouldShowError = (field, subIndex = null) => {
    if (subIndex !== null) {
      // For batch URLs
      return (
        webImportTouched.urls[subIndex] &&
        !webImportValidation.urls[subIndex]?.isValid
      );
    }
    // For regular fields
    return webImportTouched[field] && !webImportValidation[field].isValid;
  };

  const shouldShowWarning = (field, subIndex = null) => {
    if (subIndex !== null) {
      // For batch URLs
      return (
        webImportTouched.urls[subIndex] &&
        webImportValidation.urls[subIndex]?.isValid &&
        webImportValidation.urls[subIndex]?.warning
      );
    }
    // For regular fields
    return (
      webImportTouched[field] &&
      webImportValidation[field].isValid &&
      webImportValidation[field].warning
    );
  };

  const getFieldHelperText = (field, defaultText, subIndex = null) => {
    if (shouldShowError(field, subIndex)) {
      return subIndex !== null
        ? webImportValidation.urls[subIndex]?.error
        : webImportValidation[field].error;
    }
    if (shouldShowWarning(field, subIndex)) {
      return subIndex !== null
        ? webImportValidation.urls[subIndex]?.warning
        : webImportValidation[field].warning;
    }
    return defaultText;
  };

  // Check if the web import form is valid (for submit button)
  const isWebImportFormValid = () => {
    if (webImportType === "single") {
      return (
        webImportValidation.url.isValid &&
        webImportValidation.crawl_level.isValid &&
        webImportValidation.max_links.isValid &&
        webImportData.url.trim() !== ""
      );
    } else {
      // For batch import, check all URLs are valid and at least one URL is provided
      const validUrls = webImportData.urls.filter((url) => url.trim() !== "");
      const allUrlsValid = webImportValidation.urls.every(
        (validation, index) => {
          // If URL is empty, it's considered valid (optional)
          // If URL has content, it must be valid
          const url = webImportData.urls[index];
          return !url.trim() || validation.isValid;
        },
      );

      return (
        validUrls.length > 0 &&
        allUrlsValid &&
        webImportValidation.crawl_level.isValid &&
        webImportValidation.max_links_per_url.isValid
      );
    }
  };

  // Reset web import form and validation
  const resetWebImportForm = () => {
    setWebImportData({
      collection_id: collectionId,
      url: "",
      urls: [""],
      crawl_level: 1,
      max_links: 10,
      max_links_per_url: 10,
      restrict_domain: true,
    });
    setWebImportValidation({
      url: { isValid: true, error: "", warning: "" },
      urls: [{ isValid: true, error: "", warning: "" }],
      crawl_level: { isValid: true, error: "" },
      max_links: { isValid: true, error: "" },
      max_links_per_url: { isValid: true, error: "" },
    });
    setWebImportTouched({
      url: false,
      urls: [false],
      crawl_level: false,
      max_links: false,
      max_links_per_url: false,
    });
    setWebImportType("single");
  };

  // Handle web import submission
  const handleUploadFromWeb = async () => {
    setProcessingWebImport(true);
    try {
      let response;
      const payload = {
        collection_id: collectionId,
        crawl_level: webImportData.crawl_level,
        restrict_domain: webImportData.restrict_domain,
      };

      if (webImportType === "single") {
        // Single URL import
        payload.url = webImportData.url;
        payload.max_links = webImportData.max_links;
        response = await documentsApi.importFromWeb(payload);
      } else {
        // Batch URL import
        payload.urls = webImportData.urls.filter((url) => url.trim() !== "");
        payload.max_links_per_url = webImportData.max_links_per_url;
        response = await documentsApi.batchImportFromWeb(payload);
      }

      // Close web import modal
      setIsWebImportModalOpen(false);

      // Reset form
      resetWebImportForm();

      // Show success message
      const urlCount =
        webImportType === "single"
          ? 1
          : webImportData.urls.filter((url) => url.trim() !== "").length;
      toast.success(t("collections.webImportStarted", { count: urlCount }));

      // Open task progress dialog with the task ID from response
      if (response && response.task_id) {
        setTaskId(response.task_id);
        setIsTaskProgressDialogOpen(true);
      }

      // Refresh collection details after a delay
      setTimeout(() => {
        fetchCollection();
      }, 2000);
    } catch (err) {
      console.error("Error starting web import:", err);
      setError(t("collections.failedToStartWebImportConfig"));
      const details = err.response?.data?.detail ?? "";
      toast.error(t("collections.failedToStartWebImportDetails", { details }));
    } finally {
      setProcessingWebImport(false);
    }
  };

  // Handle manual refresh of documents
  const handleRefreshDocuments = async () => {
    // toast.info('Refreshing documents...');
    // Reset to first page when manually refreshing
    setPage(0);

    // Set only documentLoading to true, to avoid full page refresh
    setDocumentLoading(true);
    try {
      // Refresh collection data first, passing selectedProjects
      await fetchCollection();

      // Then refresh documents
      const data = await documentsApi.getDocumentsByCollection(collectionId, {
        skip: 0, // Always start at first page on manual refresh
        limit: rowsPerPage,
      });

      setDocuments(data);
      // toast.success('Documents refreshed successfully');
    } catch (err) {
      console.error("Error refreshing data:", err);
      toast.error(t("collections.failedToRefreshDocuments"));
    } finally {
      setDocumentLoading(false);
    }
  };

  // Render skeleton loader for collection details
  if (loading) {
    return (
      <DashboardLayout title={t("collections.title")}>
        <Container maxWidth="xl">
          <Box sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
              <Skeleton
                animation="wave"
                variant="text"
                width="50%"
                height={40}
              />
              <Skeleton
                animation="wave"
                variant="text"
                width="70%"
                height={20}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 4,
              }}
            >
              <Box sx={{ width: "60%" }}>
                <Skeleton
                  animation="wave"
                  variant="text"
                  width="40%"
                  height={40}
                />
                <Skeleton
                  animation="wave"
                  variant="text"
                  width="80%"
                  height={20}
                />
              </Box>
              <Skeleton
                animation="wave"
                variant="rectangular"
                width={150}
                height={36}
              />
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.name")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.type")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.size")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.uploaded")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.status")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(5)].map((_, index) => (
                    <TableRowSkeleton key={index} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Container>
      </DashboardLayout>
    );
  }

  if (error && !collection) {
    return (
      <DashboardLayout title={t("collections.errorTitle")}>
        <Container maxWidth="xl">
          <Box sx={{ py: 4 }}>
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate("/collections")}
                >
                  {t("collections.returnToCollections")}
                </Button>
              }
            >
              {error}
            </Alert>
          </Box>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={collection?.name || t("collections.title")}>
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          {/* Breadcrumb Navigation */}
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              color="inherit"
              sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
              onClick={() => navigate("/collections")}
            >
              {isRTL ? (
                <ArrowForwardIcon
                  fontSize="small"
                  sx={{ marginInlineEnd: 0.5 }}
                />
              ) : (
                <ArrowBackIcon fontSize="small" sx={{ marginInlineEnd: 0.5 }} />
              )}
              {t("documentCollections.title")}
            </Link>
            <Typography color="text.primary">{collection?.name}</Typography>
          </Breadcrumbs>

          {/* Collection Header */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <Typography variant="h4" component="h1" gutterBottom>
                  {collection?.name || t("collections.title")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t("documentCollections.ManageYourDocumentCollection")}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sm={4}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                  "@media (min-width: 600px) and (max-width: 835px)": {
                    flexDirection: "row",
                    flexWrap: "wrap",
                    "& > button": {
                      minWidth: "140px",
                      flex: "1 1 auto",
                      "@media (min-width: 600px) and (max-width: 700px)": {
                        minWidth: "180px",
                        padding: "0 16px",
                      },
                    },
                  },
                }}
              >
                {/* <Button
                  variant="outlined"
                  startIcon={<ChatBubbleIcon />}
                  onClick={() =>
                    navigate(`/playground?collection=${collection?.id}`)
                  }
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { sm: "190px" },
                    height: "36px",
                    fontSize: "14px",
                    padding: "20px",
                    whiteSpace: "nowrap",
                    "@media (min-width: 600px) and (max-width: 700px)": {
                      minWidth: "180px",
                      padding: "0 16px",
                    },
                  }}
                >
                  Chat with Collection
                </Button>*/}
                <Tooltip
                  title={
                    canUpload ? "" : t("collections.actionNotPermitted")
                  }
                  arrow
                >
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    <Button
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                      endIcon={<KeyboardArrowDownIcon />}
                      onClick={handleUploadMenuOpen}
                      disabled={!canUpload}
                      sx={{
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { sm: "140px" },
                        height: "36px",
                        fontSize: "14px",
                        padding: "20px",
                        whiteSpace: "nowrap",
                        "@media (min-width: 600px) and (max-width: 700px)": {
                          minWidth: "180px",
                          padding: "0 16px",
                        },
                      }}
                    >
                      {t("collections.upload")}
                    </Button>
                  </Box>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>

          {/* Upload Menu */}
          {canUpload && (
            <Menu
              anchorEl={uploadMenuAnchorEl}
              open={Boolean(uploadMenuAnchorEl)}
              onClose={handleUploadMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem onClick={handleUploadDocumentClick}>
                <ListItemIcon>
                  <CloudUploadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {t("documentCollections.uploadDocument")}
                </ListItemText>
              </MenuItem>
              <MenuItem onClick={handleExternalSourceClick}>
                <ListItemIcon>
                  <StorageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {t("documentCollections.uploadFromExternalSource")}
                </ListItemText>
              </MenuItem>
              <MenuItem onClick={handleWebImportClick}>
                <ListItemIcon>
                  <LanguageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {t("documentCollections.uploadFromWeb")}
                </ListItemText>
              </MenuItem>
            </Menu>
          )}

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 4 }}>
              {error}
            </Alert>
          )}

          {/* Documents Table */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              mt: 4,
            }}
          >
            <Typography variant="h6" component="h2">
              {t("documentCollections.documents")}
            </Typography>
            <Tooltip title={t("documentCollections.refreshDocuments")}>
              <IconButton
                onClick={handleRefreshDocuments}
                disabled={documentLoading}
                color="primary"
              >
                <RefreshIcon className="rtl:scale-x-[-1]" />
              </IconButton>
            </Tooltip>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align={isRTL ? "right" : "left"}>
                    {t("common.name")}
                  </TableCell>
                  <TableCell align={isRTL ? "right" : "left"}>
                    {t("common.type")}
                  </TableCell>
                  <TableCell align={isRTL ? "right" : "left"}>
                    {t("common.size")}
                  </TableCell>
                  <TableCell align={isRTL ? "right" : "left"}>
                    {t("common.uploaded")}
                  </TableCell>
                  <TableCell align={isRTL ? "right" : "left"}>
                    {t("common.status")}
                  </TableCell>
                  <TableCell align={isRTL ? "right" : "left"}>
                    {t("common.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documentLoading ? (
                  // Show skeleton rows when loading documents but not the whole page
                  [...Array(rowsPerPage)].map((_, index) => (
                    <TableRowSkeleton key={index} />
                  ))
                ) : documents.length > 0 ? (
                  // Show actual document data
                  documents.map((doc) => (
                    <TableRow key={doc.id} hover>
                      <TableCell
                        align={isRTL ? "right" : "left"}
                        component="th"
                        scope="row"
                      >
                        <Typography variant="body2">
                          {doc.title || doc.name}
                        </Typography>
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Typography variant="body2" color="text.secondary">
                          {doc.file_type}
                        </Typography>
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Typography variant="body2" color="text.secondary">
                          {doc.file_size_display ||
                            (doc.file_size
                              ? `${(doc.file_size / 1024).toFixed(2)} KB`
                              : "-")}
                        </Typography>
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(doc.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Chip
                          label={getDocumentStatusLabel(doc)}
                          color={getStatusColor(doc.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {/* <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/documents/${doc.id}`)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton> */}
                        {canDelete && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteDocument(doc.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  // Show empty state
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        {t("documentCollections.noDocumentsYet")}
                      </Typography>
                      {canUpload ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {t("documentCollections.uploadFirstDocument")}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {t("collections.viewAccessOnly")}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalDocuments}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: "1px solid rgba(224, 224, 224, 1)",
                ".MuiTablePagination-toolbar": {
                  padding: "16px",
                },
                ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                  {
                    margin: 0,
                  },
              }}
              labelRowsPerPage={t("documentCollections.documentsPerPage")}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count !== -1 ? count : "more than " + to}`
              }
              backIconButtonProps={{
                disabled: page === 0,
                "aria-label": "Previous Page",
              }}
              nextIconButtonProps={{
                disabled:
                  documents.length < rowsPerPage &&
                  page * rowsPerPage + documents.length >= totalDocuments,
                "aria-label": "Next Page",
              }}
            />
          </TableContainer>

          {/* Upload Documents Dialog */}
          {canUpload && (
            <UploadDocumentsDialog
              open={isUploadModalOpen}
              onOpenChange={setIsUploadModalOpen}
              collectionId={collectionId}
              onUploaded={handleDocumentsUploaded}
            />
          )}

          {/* Upload from External Source Dialog */}
          {canUpload && (
            <Dialog
              open={isExternalSourceModalOpen}
              onClose={() =>
                !processingExternalSource && setIsExternalSourceModalOpen(false)
              }
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>
                {t("collections.externalSourceTitle")}
                <IconButton
                  aria-label={t("common.close")}
                  onClick={() =>
                    !processingExternalSource &&
                    setIsExternalSourceModalOpen(false)
                  }
                  sx={{ position: "absolute", insetInlineEnd: 8, top: 8 }}
                  disabled={processingExternalSource}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    {t("collections.externalSourceDescription")}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      display: "block",
                      mt: 1,
                      p: 1,
                      backgroundColor: "warning.lighter",
                      color: "warning.dark",
                      borderRadius: 1,
                      fontWeight: "medium",
                      border: 1,
                      borderColor: "warning.main",
                    }}
                  >
                    {t("collections.documentUploadUnsupported")}
                  </Typography>

                  <Box sx={{ mt: 3, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("collections.sourceType")}
                    </Typography>

                    <TextField
                      select
                      fullWidth
                      value={externalSourceData.source_type}
                      onChange={(e) =>
                        setExternalSourceData({
                          ...externalSourceData,
                          source_type: e.target.value,
                        })
                      }
                      disabled={processingExternalSource}
                      label={t("collections.sourceType")}
                      margin="normal"
                    >
                      <MenuItem value="s3">
                        {t("collections.s3Source")}
                      </MenuItem>
                    </TextField>
                  </Box>

                  {externalSourceData.source_type === "s3" && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {t("collections.s3Configuration")}
                      </Typography>

                      <TextField
                        className="url-textfield"
                        margin="normal"
                        required
                        fullWidth
                        label={t("collections.endpointUrl")}
                        value={externalSourceData.s3_config.endpoint_url}
                        onChange={(e) =>
                          handleS3ConfigChange("endpoint_url", e.target.value)
                        }
                        disabled={processingExternalSource}
                      />

                      <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          label={t("collections.bucketName")}
                          value={externalSourceData.s3_config.bucket_name}
                          onChange={(e) =>
                            handleS3ConfigChange("bucket_name", e.target.value)
                          }
                          disabled={processingExternalSource}
                        />

                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          label={t("collections.prefixFolderPath")}
                          value={externalSourceData.s3_config.prefix}
                          onChange={(e) =>
                            handleS3ConfigChange("prefix", e.target.value)
                          }
                          disabled={processingExternalSource}
                        />
                      </Box>

                      <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          label={t("collections.accessKey")}
                          value={externalSourceData.s3_config.access_key}
                          onChange={(e) =>
                            handleS3ConfigChange("access_key", e.target.value)
                          }
                          disabled={processingExternalSource}
                        />

                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          label={t("collections.secretKey")}
                          type="password"
                          value={externalSourceData.s3_config.secret_key}
                          onChange={(e) =>
                            handleS3ConfigChange("secret_key", e.target.value)
                          }
                          disabled={processingExternalSource}
                        />
                      </Box>

                      <TextField
                        margin="normal"
                        required
                        label={t("collections.region")}
                        value={externalSourceData.s3_config.region}
                        onChange={(e) =>
                          handleS3ConfigChange("region", e.target.value)
                        }
                        disabled={processingExternalSource}
                        sx={{ width: "50%" }}
                      />
                    </Box>
                  )}
                </Box>
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                  onClick={() => setIsExternalSourceModalOpen(false)}
                  disabled={processingExternalSource}
                  color="secondary"
                  variant="text"
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  onClick={handleUploadFromExternalSource}
                  disabled={
                    processingExternalSource ||
                    !externalSourceData.source_type ||
                    (externalSourceData.source_type === "s3" &&
                      (!externalSourceData.s3_config.bucket_name ||
                        !externalSourceData.s3_config.prefix ||
                        !externalSourceData.s3_config.access_key ||
                        !externalSourceData.s3_config.secret_key ||
                        !externalSourceData.s3_config.region ||
                        !externalSourceData.s3_config.endpoint_url))
                  }
                  variant="contained"
                  startIcon={
                    processingExternalSource && (
                      <CircularProgress size={20} color="inherit" />
                    )
                  }
                >
                  {processingExternalSource
                    ? t("collections.processing")
                    : t("collections.startImport")}
                </Button>
              </DialogActions>
            </Dialog>
          )}

          {/* Upload from Web Dialog */}
          {canUpload && (
            <Dialog
              open={isWebImportModalOpen}
              onClose={() =>
                !processingWebImport && setIsWebImportModalOpen(false)
              }
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>
                {t("collections.webTitle")}
                <IconButton
                  aria-label={t("common.close")}
                  onClick={() =>
                    !processingWebImport && setIsWebImportModalOpen(false)
                  }
                  sx={{ position: "absolute", insetInlineEnd: 8, top: 8 }}
                  disabled={processingWebImport}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    {t("collections.webDescription")}
                  </Typography>

                  <Box sx={{ mt: 3, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("collections.importType")}
                    </Typography>

                    <TextField
                      select
                      fullWidth
                      value={webImportType}
                      onChange={(e) => setWebImportType(e.target.value)}
                      disabled={processingWebImport}
                      label={t("collections.importType")}
                      margin="normal"
                    >
                      <MenuItem value="single">
                        {t("collections.singleUrlWithCrawling")}
                      </MenuItem>
                      <MenuItem value="batch">
                        {t("collections.multipleUrls")}
                      </MenuItem>
                    </TextField>
                  </Box>

                  {webImportType === "single" ? (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {t("collections.singleUrlConfiguration")}
                      </Typography>

                      <TextField
                        className="url-textfield"
                        margin="normal"
                        required
                        fullWidth
                        label={t("common.url")}
                        value={webImportData.url}
                        onChange={(e) =>
                          handleWebImportChange("url", e.target.value)
                        }
                        onBlur={() => handleWebImportBlur("url")}
                        disabled={processingWebImport}
                        placeholder="https://example.com/page"
                        error={shouldShowError("url")}
                        helperText={getFieldHelperText(
                          "url",
                          t("collections.urlHelper"),
                        )}
                        FormHelperTextProps={{
                          sx: {
                            color: shouldShowWarning("url")
                              ? "warning.main"
                              : undefined,
                          },
                        }}
                      />

                      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                        <TextField
                          margin="normal"
                          fullWidth
                          label={t("collections.crawlLevel")}
                          type="number"
                          value={webImportData.crawl_level}
                          onChange={(e) =>
                            handleWebImportChange(
                              "crawl_level",
                              parseInt(e.target.value, 10),
                            )
                          }
                          onBlur={() => handleWebImportBlur("crawl_level")}
                          disabled={processingWebImport}
                          inputProps={{ min: 1, max: 5 }}
                          error={shouldShowError("crawl_level")}
                          helperText={getFieldHelperText(
                            "crawl_level",
                            t("collections.crawlLevelHelper"),
                          )}
                        />

                        <TextField
                          margin="normal"
                          fullWidth
                          label={t("collections.maxLinks")}
                          type="number"
                          value={webImportData.max_links}
                          onChange={(e) =>
                            handleWebImportChange(
                              "max_links",
                              parseInt(e.target.value, 10),
                            )
                          }
                          onBlur={() => handleWebImportBlur("max_links")}
                          disabled={processingWebImport}
                          inputProps={{ min: 1, max: 100 }}
                          error={shouldShowError("max_links")}
                          helperText={getFieldHelperText(
                            "max_links",
                            t("collections.maxLinksHelper"),
                          )}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {t("collections.multipleUrlsConfiguration")}
                      </Typography>

                      <Box sx={{ mb: 2 }}>
                        {webImportData.urls.map((url, index) => (
                          <Box
                            key={index}
                            sx={{ display: "flex", gap: 1, mb: 1 }}
                          >
                            <TextField
                              fullWidth
                              label={`URL ${index + 1}`}
                              value={url}
                              onChange={(e) =>
                                handleBatchUrlChange(index, e.target.value)
                              }
                              onBlur={() => handleBatchUrlBlur(index)}
                              disabled={processingWebImport}
                              placeholder="https://example.com/page"
                              error={shouldShowError("urls", index)}
                              helperText={getFieldHelperText("urls", "", index)}
                              FormHelperTextProps={{
                                sx: {
                                  color: shouldShowWarning("urls", index)
                                    ? "warning.main"
                                    : undefined,
                                },
                              }}
                            />
                            {webImportData.urls.length > 1 && (
                              <IconButton
                                onClick={() => handleRemoveBatchUrl(index)}
                                disabled={processingWebImport}
                                color="error"
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Box>
                        ))}

                        <Button
                          startIcon={<AddIcon />}
                          onClick={handleAddBatchUrl}
                          disabled={processingWebImport}
                          variant="outlined"
                          size="small"
                          sx={{ mt: 1 }}
                        >
                          {t("collections.addUrl")}
                        </Button>
                      </Box>

                      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                        <TextField
                          margin="normal"
                          fullWidth
                          label={t("collections.crawlLevel")}
                          type="number"
                          value={webImportData.crawl_level}
                          onChange={(e) =>
                            handleWebImportChange(
                              "crawl_level",
                              parseInt(e.target.value, 10),
                            )
                          }
                          onBlur={() => handleWebImportBlur("crawl_level")}
                          disabled={processingWebImport}
                          inputProps={{ min: 1, max: 5 }}
                          error={shouldShowError("crawl_level")}
                          helperText={getFieldHelperText(
                            "crawl_level",
                            t("collections.crawlLevelHelper"),
                          )}
                        />

                        <TextField
                          margin="normal"
                          fullWidth
                          label={t("collections.maxLinksPerUrl")}
                          type="number"
                          value={webImportData.max_links_per_url}
                          onChange={(e) =>
                            handleWebImportChange(
                              "max_links_per_url",
                              parseInt(e.target.value, 10),
                            )
                          }
                          onBlur={() =>
                            handleWebImportBlur("max_links_per_url")
                          }
                          disabled={processingWebImport}
                          inputProps={{ min: 1, max: 100 }}
                          error={shouldShowError("max_links_per_url")}
                          helperText={getFieldHelperText(
                            "max_links_per_url",
                            t("collections.maxLinksPerUrlHelper"),
                          )}
                        />
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("collections.advancedOptions")}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                      <Checkbox
                        id="restrict-domain"
                        checked={webImportData.restrict_domain}
                        onChange={(e) =>
                          handleWebImportChange(
                            "restrict_domain",
                            e.target.checked,
                          )
                        }
                        disabled={processingWebImport}
                        style={{ marginInlineEnd: "8px" }}
                      />
                      <label htmlFor="restrict-domain">
                        <Typography variant="body2">
                          {t("collections.restrictSameDomain")}
                        </Typography>
                      </label>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ marginInlineStart: 3 }}
                    >
                      {t("collections.restrictSameDomainHelper")}
                    </Typography>
                  </Box>
                </Box>
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                  onClick={() => {
                    setIsWebImportModalOpen(false);
                    resetWebImportForm();
                  }}
                  disabled={processingWebImport}
                  color="secondary"
                  variant="text"
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  onClick={handleUploadFromWeb}
                  disabled={processingWebImport || !isWebImportFormValid()}
                  variant="contained"
                  startIcon={
                    processingWebImport && (
                      <CircularProgress size={20} color="inherit" />
                    )
                  }
                >
                  {processingWebImport
                    ? t("collections.processing")
                    : t("collections.startImport")}
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </Box>
      </Container>

      {/* Task Progress Dialog */}
      <TaskProgressDialog
        taskId={taskId}
        collectionId={collectionId}
        open={isTaskProgressDialogOpen}
        onClose={() => setIsTaskProgressDialogOpen(false)}
      />

      {/* Document Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDocumentDelete}
        PaperProps={{
          sx: {
            borderRadius: 1,
          },
        }}
      >
        <DialogTitle>{t("collections.confirmDocumentDeletion")}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 1 }}>
            {t("common.cannotBeUndone")}
          </Alert>
          <Typography variant="body1">
            {t("collections.deleteDocumentConfirm", {
              name: documentToDelete?.title || documentToDelete?.name,
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDocumentDelete}
            color="secondary"
            variant="text"
            disabled={deletingDocument}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDocumentDelete}
            color="error"
            variant="contained"
            sx={{ borderRadius: 1 }}
            disabled={deletingDocument}
            startIcon={
              deletingDocument ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {deletingDocument ? t("common.deleting") : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default CollectionDetailPage;
